import { Component, ElementRef, HostBinding, Input, OnInit, ViewChild } from '@angular/core';
import { DsButtonIntent, DsButtonVariants, DsModalComponent, Icons } from '@levelaccess/design-system';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-ds-action-confirmation-modal',
  templateUrl: './ds-action-confirmation-modal.component.html',
  styleUrls: ['./ds-action-confirmation-modal.component.scss'],
  standalone: false,
})
export class DsActionConfirmationModalComponent implements OnInit {
  public readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public readonly DsButtonIntent: typeof DsButtonIntent = DsButtonIntent;
  public readonly Icons: typeof Icons = Icons;

  @Input() public title: string;
  @Input() public message: string;
  @Input() public confirmLabel: string;
  @Input() public cancelLabel: string;
  @Input() public closeLabel: string;
  @Input() public maxWidthPercentage: number = 45;
  @Input() public closeAllModals: boolean = false;
  /**
   * Shows the default icon-only X of ds-modal instead of the text close button, with `closeLabel` as its tooltip and
   * aria-label (ds-modal would otherwise use "Close <title>"). Off by default, so existing callers keep their look.
   */
  @Input() @HostBinding('class.icon-only-close') public iconOnlyClose: boolean = false;
  /** Names of the items the action affects, listed under the message. */
  @Input() public listItems: string[] = [];
  /**
   * Makes the cancel button the primary "keep my work" button with initial focus, and the confirm button a
   * secondary destructive button. Closing with X or Esc behaves like the cancel button.
   */
  @Input() public keepButtonPrimary: boolean = false;
  /** The cancel button is a second way forward, not a way out: it closes the modal with 'secondary'. X and Esc still dismiss. */
  @Input() public secondaryClosesModal: boolean = false;

  @ViewChild(DsModalComponent, { static: true }) private readonly dsModal: DsModalComponent;

  @ViewChild('keepButton', { read: ElementRef })
  protected set keepButton(button: ElementRef<HTMLButtonElement> | undefined) {
    if (this.keepButtonPrimary) {
      button?.nativeElement.focus();
    }
  }

  constructor(public modal: NgbActiveModal) {}

  public ngOnInit(): void {
    // ds-modal builds the tooltip and aria-label of its X as "Close <title>" and has no input to change it.
    // The override returns the original value unless iconOnlyClose is set with a closeLabel, so other modals are unaffected.
    const original: PropertyDescriptor | undefined = Object.getOwnPropertyDescriptor(
      DsModalComponent.prototype,
      'closeButtonAriaLabel',
    );
    Object.defineProperty(this.dsModal, 'closeButtonAriaLabel', {
      configurable: true,
      get: (): string =>
        this.iconOnlyClose && this.closeLabel ? this.closeLabel : (original?.get?.call(this.dsModal) as string),
    });
  }

  public onCancel(): void {
    this.modal.dismiss();
  }

  public onSecondary(): void {
    if (this.secondaryClosesModal) {
      this.modal.close('secondary');
    } else {
      this.onCancel();
    }
  }

  public onConfirm(): void {
    this.modal.close();
  }
}
